import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';

export type UpdateEmailProfile = {
  id: string;
  name: string;
  email_id: string;
  email_otp: string;
  password: string;
};

export type UpdatePhoneProfile = {
  id: string;
  name: string;
  mobile_number: string;
  mobile_otp: string;
};

export type UpdateAvatarProfile = {
  id: string;
  email: string;
  name: string;
  avatar: string;
  is_communication: boolean;
};

export type UpdateProfilePayload =
  | UpdateEmailProfile
  | UpdatePhoneProfile
  | UpdateAvatarProfile;

/**
 * Name-only profile update. Hits the same PUT /user/v1/profile/{id} endpoint
 * but carries no OTP fields — used by the verify-name flow where the backend
 * does not require OTP auth to set the student's name.
 */
export type UpdateProfileNamePayload = {
  id: string;
  name: string;
};

export type UpdateProfileResponse = {
  status: string;
  message: string;
};

export const updateProfile = (
  data: UpdateProfilePayload
): Promise<UpdateProfileResponse> => {
  return axios.put(`/user/v1/profile/${data.id}`, data);
};

export const updateProfileName = (
  data: UpdateProfileNamePayload
): Promise<UpdateProfileResponse> => {
  return axios.put(`/user/v1/profile-name/${data.id}`, data);
};

// Mock handler helpers
const createErrorResponse = (
  message: string,
  status = 400
): HttpResponse<UpdateProfileResponse> => {
  return HttpResponse.json(
    {
      status: 'Failure',
      message,
    },
    { status }
  );
};

const validateOtpFormat = (otp: string): boolean => {
  return /^\d{6}$/.test(otp);
};

const validatePhoneFormat = (phone: string): boolean => {
  return /^\+\d+-\d+$/.test(phone);
};

const validateEmailUpdate = (
  body: Omit<UpdateEmailProfile, 'id'>
): HttpResponse<UpdateProfileResponse> | null => {
  if (!body.email_id || !body.email_otp || !body.password) {
    return createErrorResponse('Email ID, OTP, and password are required');
  }

  if (!validateOtpFormat(body.email_otp)) {
    return createErrorResponse('Invalid OTP format');
  }

  return null;
};

const validatePhoneUpdate = (
  body: Omit<UpdatePhoneProfile, 'id'>
): HttpResponse<UpdateProfileResponse> | null => {
  if (!body.mobile_number || !body.mobile_otp) {
    return createErrorResponse('Mobile number and OTP are required');
  }

  if (!validatePhoneFormat(body.mobile_number)) {
    return createErrorResponse('Invalid mobile number format');
  }

  if (!validateOtpFormat(body.mobile_otp)) {
    return createErrorResponse('Invalid OTP format');
  }

  return null;
};

const validateAvatarUpdate = (
  body: Omit<UpdateAvatarProfile, 'id'>
): HttpResponse<UpdateProfileResponse> | null => {
  if (!body.email || !body.avatar) {
    return createErrorResponse('Email and avatar are required');
  }

  return null;
};

// Mock handler
const mockUpdateProfileResponse: UpdateProfileResponse = {
  status: 'Success',
  message: 'Profile updated successfully',
};

export const updateProfileMockHandler = http.put(
  `${env.API_URL}/user/v1/profile/:id`,
  async ({ request }): Promise<HttpResponse<UpdateProfileResponse>> => {
    const body = (await request.json()) as UpdateProfilePayload;

    // Validate required fields
    if (!body.name) {
      return createErrorResponse('Name is required');
    }

    // Validate based on update type
    if ('email_id' in body) {
      const error = validateEmailUpdate(body);
      if (error) return error;
    }

    if ('mobile_number' in body) {
      const error = validatePhoneUpdate(body);
      if (error) return error;
    }

    if ('avatar' in body) {
      const error = validateAvatarUpdate(body);
      if (error) return error;
    }

    return HttpResponse.json(mockUpdateProfileResponse);
  }
);

// No-OTP name update lives on its own endpoint.
export const updateProfileNameMockHandler = http.put(
  `${env.API_URL}/user/v1/profile-name/:id`,
  async ({ request }): Promise<HttpResponse<UpdateProfileResponse>> => {
    const body = (await request.json()) as UpdateProfileNamePayload;

    if (!body.name) {
      return createErrorResponse('Name is required');
    }

    return HttpResponse.json(mockUpdateProfileResponse);
  }
);
